import { useCallback, useEffect, useState } from 'react'
import { useCurrentStore, useStoreBizHours } from 'hooks'
import {
  Order,
  OrderType,
  MenuCategory,
  MenuItem,
  OrderItem,
  SubmitOrderResponse,
  OrderingState,
  SubmitOrderError,
  DeliveryByOwnerInfo,
  timestampFormat,
} from 'models'
import { useDispatch, useSelector } from 'react-redux'
import {
  setCartButtonVisible,
  setCartVisible,
  setEditOrderDialogVisible,
  setEditingOrderItem,
  setOrder,
  setSubmitOrderState,
  setContinueAsGuest,
} from 'store/OrderingSlice'
import {
  checkIsAsapEnabled,
  checkCanSubmitOrder,
  newOrder,
  newOrderItem,
  updateOrderAmounts,
  checkOrderByTimeChange,
  checkIsSplitTip,
  calculateOrderTime,
} from 'services'
import { RootState } from 'store'
import {
  useLazyGetDeliveryQuoteQuery,
  usePostOrderMutation,
} from 'apis/StoreApi'
import { useTranslation } from 'react-i18next'
import { useLazyGetPromoCodeByNameQuery } from 'apis/PromoCodeApi'

interface CreateOrderParams {
  orderType: OrderType
  tableId?: string
  isAsap?: boolean
}
type CreateOrderFn = (params: CreateOrderParams) => Order

export const useOrdering = () => {
  const dispatch = useDispatch()
  const { order, submitOrderState } = useSelector<RootState>(
    (state) => state.ordering,
  ) as OrderingState
  const { store, isAcceptingOrder } = useCurrentStore()
  const { isOpening } = useStoreBizHours()
  const { t } = useTranslation()

  const canSubmitOrder = checkCanSubmitOrder(isAcceptingOrder, isOpening, order)

  const isAsapEnabled = checkIsAsapEnabled(isOpening, store, order?.order_type)

  const isSplitTip = checkIsSplitTip(store, order?.order_type)

  const createOrder = useCallback(
    ({ orderType, tableId, isAsap }: CreateOrderParams) => {
      if (!store) return
      const order = newOrder(store, orderType, tableId, isAsap)
      dispatch(setOrder(order))
    },
    [store, dispatch],
  ) as CreateOrderFn

  const saveOrder = useCallback(
    (order: Order) => {
      if (!store) return order
      const updatedOrder = updateOrderAmounts(order, store)
      dispatch(setOrder(updatedOrder))
      return updatedOrder
    },
    [dispatch, store],
  )

  const saveOrderProps = useCallback(
    (fields: Partial<Order>) => {
      if (order) {
        saveOrder({ ...order, ...fields })
      }
    },
    [saveOrder, order],
  )

  const clearOrder = useCallback(() => {
    dispatch(setOrder(undefined))
  }, [dispatch])

  const [postOrder] = usePostOrderMutation()

  const setSubmitError = useCallback(
    (error: SubmitOrderError) =>
      dispatch(
        setSubmitOrderState({
          isSubmitting: false,
          isError: true,
          isSuccess: false,
          error,
          result: undefined,
        }),
      ),
    [dispatch],
  )
  const setSubmitSuccess = useCallback(
    (result: SubmitOrderResponse) => {
      dispatch(setContinueAsGuest(false))
      dispatch(
        setSubmitOrderState({
          isSubmitting: false,
          isError: false,
          isSuccess: true,
          error: undefined,
          result: result as any,
        }),
      )
    },
    [dispatch],
  )

  const setIsSubmitting = useCallback(
    () =>
      dispatch(
        setSubmitOrderState({
          isSubmitting: true,
          isError: false,
          isSuccess: false,
          error: undefined,
          result: undefined,
        }),
      ),
    [dispatch],
  )

  const clearSubmitOrderState = useCallback(
    () =>
      dispatch(
        setSubmitOrderState({
          isSubmitting: false,
          isError: false,
          isSuccess: false,
          error: undefined,
          result: undefined,
        }),
      ),
    [dispatch],
  )

  const [wsState, setWsState] = useState({
    ws: undefined as WebSocket | undefined,
    timeoutHandle: undefined as number | undefined,
  })
  const clearState = useCallback(() => {
    if (wsState.ws) {
      try {
        wsState.ws.close()
      } catch (e) {
        console.info(`[WS] error closing websocket: ${e}`)
      }
    }
    if (wsState.timeoutHandle) clearTimeout(wsState.timeoutHandle)
    setWsState({} as any)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  const listenForPayingStatus = useCallback(
    (orderId: string) => {
      console.debug(`[WS] connecting to order status endpoint for ${orderId}`)

      clearState()

      const ws = new WebSocket(process.env.REACT_APP_WS_API_URL as string)
      let timeoutHandle
      ws.onopen = () => {
        console.debug(`[WS] connected to order status endpoint for ${orderId}`)
        ws?.send(JSON.stringify({ action: 'addClient', taskId: orderId }))
        timeoutHandle = setTimeout(
          async () => {
            // await this.recheckOrderStatus(this.order.unique_id, ignorePayment);
            clearState()
          },
          2 * 60 * 1000,
        )
      }
      ws.onerror = async (err) => {
        console.error(`[WS] error: ${err.type}`)
        clearState()
      }
      ws.onclose = async () => {
        console.debug(`[WS] closed`)
        clearState()
      }
      ws.onmessage = async (message) => {
        console.debug(`[WS] message received: ${message.data}`)
        if (message.data === undefined || message.data === null) return

        const res = JSON.parse(message.data) as any
        if (res.type !== 'paymentResult') {
          console.debug(`[WS] received wrong message type: ${res.type}`)
          return
        }

        const { status, message: wsMessage } = res as SubmitOrderResponse
        switch (status) {
          case 'success':
            setSubmitSuccess(res as SubmitOrderResponse)
            clearState()
            break
          case 'error':
            setSubmitError({
              message: wsMessage ?? 'Unknown error',
              detail: '',
            })
            clearState()
            break
          default:
            break
        }
      }
      setWsState({ ws, timeoutHandle })
    },
    [clearState, setSubmitSuccess, setSubmitError],
  )

  const submitOrder = useCallback(
    async (order: Order, submitToken?: string) => {
      if (!submitToken || submitToken.length === 0) {
        console.error('Invalid submit token')
        return
      }

      const [isChangeOrderByTime, newOrder] = checkOrderByTimeChange(
        store,
        order,
      )
      if (isChangeOrderByTime) {
        dispatch(setOrder(newOrder))
        setSubmitError({
          message: t(
            'Sorry! Some of the goods in your order are currently unavailable. Please recheck before submitting again.',
          ),
          detail: '',
        })
        return
      }

      // Calculate order time
      const updatedOrder = { ...order }
      if ((order.is_asap || !order.time) && !order.table) {
        const orderTime = calculateOrderTime(store, order)
        updatedOrder.time = orderTime.format(timestampFormat)
        updatedOrder.timeIsoFormat = orderTime.format()
      }

      try {
        setIsSubmitting()
        const res = await postOrder({
          storeId: store?.id ?? '',
          submitToken,
          order: updatedOrder,
        })
        if ('error' in res) {
          const error = res.error as any
          if ('data' in error) {
            setSubmitError(error.data)
          } else {
            setSubmitError(error)
          }
          return
        }
        const { status, message, orderId } = res.data as SubmitOrderResponse
        switch (status) {
          case 'awaiting_payment':
            setSubmitSuccess(res.data as SubmitOrderResponse)
            break
          case 'paying':
            if (!orderId) {
              setSubmitError({
                message: 'Invalid server response. Missing order id.',
                detail: '',
              })
            } else {
              listenForPayingStatus(orderId)
            }
            break
          case 'error':
            setSubmitError({
              message: message ?? 'Unknown error',
              detail: '',
            })
            break
          default:
            setSubmitError({
              message: `Invalid server response. Expecting either awaiting_payment or paying status but got ${status}`,
              detail: '',
            })
        }
      } catch (error: Error | any) {
        setSubmitError({
          message: error.message ?? '',
          detail: JSON.stringify(error),
        })
      }
    },
    [
      postOrder,
      store,
      listenForPayingStatus,
      setIsSubmitting,
      setSubmitError,
      setSubmitSuccess,
      dispatch,
      t,
    ],
  )

  return {
    order,
    canSubmitOrder,
    isAsapEnabled,
    isSplitTip,
    submitOrderState,
    createOrder,
    clearSubmitOrderState,
    saveOrder,
    saveOrderProps,
    clearOrder,
    submitOrder,
  }
}

export const useCart = (): {
  showCart: () => void
  hideCart: () => void
  isCartVisible: boolean
} => {
  const dispatch = useDispatch()
  const isCartVisible = useSelector(
    (state: RootState) => state.ordering.isCartVisible,
  )
  const showCart = () => {
    dispatch(setCartVisible(true))
  }
  const hideCart = () => {
    dispatch(setCartVisible(false))
  }

  return {
    showCart,
    hideCart,
    isCartVisible,
  }
}

export const useCartButton = (): {
  showCartButton: () => void
  hideCartButton: () => void
  isCartButtonVisible: boolean
} => {
  const dispatch = useDispatch()
  const isCartButtonVisible = useSelector(
    (state: RootState) => state.ordering.isCartButtonVisible,
  )
  const showCartButton = () => {
    dispatch(setCartButtonVisible(true))
  }
  const hideCartButton = () => {
    dispatch(setCartButtonVisible(false))
  }

  return {
    showCartButton,
    hideCartButton,
    isCartButtonVisible,
  }
}

export const useEditOrderItem = () => {
  const dispatch = useDispatch()
  const editingOrderItem = useSelector(
    (state: RootState) => state.ordering.editingOrderItem,
  ) as OrderItem

  const addOrderItem = (category: MenuCategory, item: MenuItem) => {
    const orderItem = newOrderItem(category, item)
    editOrderItem(orderItem)
  }

  const editOrderItem = (orderItem: OrderItem) => {
    dispatch(setEditingOrderItem(orderItem))
  }

  const clearEditingOrderItem = () => {
    dispatch(setEditingOrderItem(undefined))
  }
  return {
    addOrderItem,
    editOrderItem,
    clearEditingOrderItem,
    editingOrderItem,
  }
}

export const useEditOrderDialog = () => {
  const dispatch = useDispatch()
  const isEditOrderDialogVisible = useSelector(
    (state: RootState) => state.ordering.isEditOrderDialogVisible,
  ) as boolean

  const showEditOrderDialog = () => {
    dispatch(setEditOrderDialogVisible(true))
  }
  const hideEditOrderDialog = () => {
    dispatch(setEditOrderDialogVisible(false))
  }
  return {
    showEditOrderDialog,
    hideEditOrderDialog,
    isEditOrderDialogVisible,
  }
}

export const useDeliveryQuote = () => {
  const [getDeliveryQuote, { isLoading, data, error, isFetching }] =
    useLazyGetDeliveryQuoteQuery()

  const [errorMessage, setErrorMessage] = useState<string | undefined>()
  const { store } = useCurrentStore()

  const createDeliveryQuote = useCallback(
    (destination: string, orderTotal: number, dropoffTime?: string) => {
      if (destination.length === 0 || orderTotal === 0) return

      const storeid = store?.id ?? ''
      const origin =
        store?.delivery_settings?.delivery_from_address?.description ?? ''
      getDeliveryQuote({
        storeid,
        origin,
        destination,
        dropoff_time: dropoffTime,
        order_value: orderTotal,
      })
    },
    [
      getDeliveryQuote,
      store?.delivery_settings?.delivery_from_address?.description,
      store?.id,
    ],
  )

  useEffect(() => {
    if (!error) {
      setErrorMessage(undefined)
      return
    }
    if ('data' in error) {
      const data = error.data as any
      if ('message' in data) {
        setErrorMessage(data.message)
        return
      }
    }
    setErrorMessage('Error getting delivery fee.')
  }, [error])

  return {
    createDeliveryQuote,
    isLoading: isLoading || isFetching,
    deliveryQuote: data,
    errorMessage,
  }
}

export const useDeliveryByOwner = () => {
  const { t } = useTranslation()
  const { store } = useCurrentStore()
  const { order } = useOrdering()
  const deliverySettings = store?.delivery_settings

  const calculateDeliveryFee = useCallback(
    (distance: number): DeliveryByOwnerInfo => {
      if (!deliverySettings || !order) return {}

      if (distance < 0) {
        return {
          errorMessage: t('Delivery cannot be made to this address.'),
        }
      }

      const maxDeliveryDistance =
        deliverySettings?.maximum_delivery_distance ?? 0
      if (distance > maxDeliveryDistance) {
        return {
          errorMessage: t('Address is out of delivery distance.'),
        }
      }

      const deliveryDistanceText =
        distance <= 1
          ? t('less than 1 mile')
          : t('approximately {{distance}} miles', {
              distance,
            })

      let deliveryFeeAmount = 0
      const freeDeliveryAmount = deliverySettings?.free_delivery_amount ?? 0
      if (freeDeliveryAmount > 0 && order.subtotal >= freeDeliveryAmount) {
        deliveryFeeAmount = 0
      } else if (deliverySettings?.use_fixed_delivery_fee === true) {
        deliveryFeeAmount = deliverySettings?.fixed_delivery_fee ?? 0
      } else {
        let steps = deliverySettings?.delivery_fees ?? []
        let index = steps.findIndex((s) => distance < (s.distance ?? 0))
        if (index < 0) {
          // If it could not be found, means that it is greater than the greatest
          // and thus the greatest value should be used
          index = steps.length - 1
        } else if (index > 0) {
          // Take the previous step, in case it is out of range
          // then take the first step
          index = index - 1
        }
        deliveryFeeAmount = steps[index]?.fee ?? 0
      }
      return {
        deliveryDistanceText,
        deliveryFeeAmount,
      }
    },
    [deliverySettings, order, t],
  )

  return { calculateDeliveryFee }
}

export const usePromoCode = () => {
  const [
    getPromoCodeByName,
    { isLoading, data, error, isFetching, isSuccess },
  ] = useLazyGetPromoCodeByNameQuery()

  const [errorMessage, setErrorMessage] = useState<string | undefined>()
  const { store } = useCurrentStore()
  const { saveOrderProps } = useOrdering()

  const applyPromoCode = useCallback(
    (name: string) => {
      if (name && store) {
        getPromoCodeByName({ storeId: store.id, name })
      }
    },
    [store, getPromoCodeByName],
  )

  useEffect(() => {
    if (!error) {
      setErrorMessage(undefined)
      return
    }
    if ('data' in error) {
      const data = error.data as any
      if ('message' in data) {
        setErrorMessage(data.message)
        return
      }
    }
    setErrorMessage('Invalid promotion code!')
  }, [error])

  useEffect(() => {
    if (error) {
      saveOrderProps({ discount: undefined })
    } else if (data) {
      saveOrderProps({ discount: data })
    }
  }, [data, error]) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    applyPromoCode,
    isLoading: isLoading || isFetching,
    isSuccess,
    errorMessage,
  }
}
